/** @format */

import React, { useCallback, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Drag (or tap, or use the arrow keys) to compare two photos of the same
// room. Both images must share one perspective and size; the "after" image is
// the base and the "before" image is clipped over it from the left.
//
// `touch-action: pan-y` lets a vertical swipe still scroll the page on a
// phone, while a sideways drag moves the divider. The range input is the
// keyboard and screen-reader control; it is visually hidden.
const BeforeAfterSlider = ({
	before,
	after,
	beforeAlt,
	afterAlt,
	beforeLabel = "Before",
	afterLabel = "After",
	initial = 50,
	aspect = "aspect-[3/2]",
	className = "",
	onFirstMove,
}) => {
	const [pos, setPos] = useState(initial);
	const box = useRef(null);
	const dragging = useRef(false);
	const moved = useRef(false);

	const noteMove = useCallback(() => {
		if (!moved.current) {
			moved.current = true;
			onFirstMove?.();
		}
	}, [onFirstMove]);

	const setFromClientX = useCallback(
		(clientX) => {
			const rect = box.current?.getBoundingClientRect();
			if (!rect || rect.width === 0) return;
			const pct = ((clientX - rect.left) / rect.width) * 100;
			setPos(Math.min(100, Math.max(0, pct)));
			noteMove();
		},
		[noteMove]
	);

	return (
		<div
			ref={box}
			className={`relative w-full select-none overflow-hidden bg-[#F1EFEA] ${aspect} ${className}`}
			style={{ touchAction: "pan-y" }}
			onPointerDown={(e) => {
				dragging.current = true;
				e.currentTarget.setPointerCapture?.(e.pointerId);
				setFromClientX(e.clientX);
			}}
			onPointerMove={(e) => dragging.current && setFromClientX(e.clientX)}
			onPointerUp={() => (dragging.current = false)}
			onPointerCancel={() => (dragging.current = false)}
		>
			<img
				src={after}
				alt={afterAlt}
				draggable={false}
				className="absolute inset-0 h-full w-full object-cover"
			/>
			<img
				src={before}
				alt={beforeAlt}
				draggable={false}
				className="absolute inset-0 h-full w-full object-cover"
				style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
			/>

			<span className="pointer-events-none absolute left-3 top-3 bg-[#141414]/75 px-2.5 py-1 text-[11px] font-medium uppercase tracking-[0.14em] text-white">
				{beforeLabel}
			</span>
			<span className="pointer-events-none absolute right-3 top-3 bg-white/90 px-2.5 py-1 text-[11px] font-medium uppercase tracking-[0.14em] text-[#141414]">
				{afterLabel}
			</span>

			{/* Divider and handle */}
			<div
				className="pointer-events-none absolute inset-y-0 w-0.5 -translate-x-1/2 bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.15)]"
				style={{ left: `${pos}%` }}
				aria-hidden="true"
			>
				<div className="absolute left-1/2 top-1/2 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-[#141414] shadow-lg">
					<ChevronLeft size={16} strokeWidth={2} />
					<ChevronRight size={16} strokeWidth={2} />
				</div>
			</div>

			<input
				type="range"
				min="0"
				max="100"
				step="1"
				value={Math.round(pos)}
				onChange={(e) => {
					setPos(Number(e.target.value));
					noteMove();
				}}
				aria-label={`Compare ${beforeLabel.toLowerCase()} and ${afterLabel.toLowerCase()}`}
				className="sr-only"
			/>
		</div>
	);
};

export default BeforeAfterSlider;
